/**
 * API Response Cache
 *
 * Simple in-memory cache for API responses with TTL (time-to-live).
 * Used to reduce redundant API calls for frequently accessed data.
 */

// Default cache configuration
const DEFAULT_TTL_MS = 30000; // 30 seconds
const MAX_CACHE_SIZE = 100; // Maximum number of cached entries

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.accessOrder = []; // Track access order for LRU eviction
  }

  /**
   * Generates a cache key from parameters
   * @param {string} prefix - Cache key prefix (e.g., 'client', 'tags')
   * @param {Object|string|number} params - Parameters to include in key
   * @returns {string} Cache key
   */
  generateKey(prefix, params) {
    if (typeof params === 'object') {
      return `${prefix}:${JSON.stringify(params)}`;
    }
    return `${prefix}:${params}`;
  }

  /**
   * Gets a cached value if not expired
   * @param {string} key - Cache key
   * @returns {*} Cached value or undefined if not found/expired
   */
  get(key) {
    const entry = this.cache.get(key);

    if (!entry) {
      return undefined;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      return undefined;
    }

    // Update access order for LRU
    this.updateAccessOrder(key);

    return entry.value;
  }

  /**
   * Sets a value in the cache
   * @param {string} key - Cache key
   * @param {*} value - Value to cache
   * @param {number} ttlMs - Time-to-live in milliseconds
   */
  set(key, value, ttlMs = DEFAULT_TTL_MS) {
    // Evict oldest entries if cache is full
    this.evictIfNeeded();

    const entry = {
      value,
      expiresAt: Date.now() + ttlMs,
      createdAt: Date.now(),
    };

    this.cache.set(key, entry);
    this.updateAccessOrder(key);
  }

  /**
   * Deletes a specific cache entry
   * @param {string} key - Cache key to delete
   */
  delete(key) {
    this.cache.delete(key);
    this.accessOrder = this.accessOrder.filter(k => k !== key);
  }

  /**
   * Invalidates cache entries matching a pattern
   * @param {string} prefix - Key prefix to match
   */
  invalidateByPrefix(prefix) {
    const keysToDelete = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.delete(key));
  }

  /**
   * Clears all cache entries
   */
  clear() {
    this.cache.clear();
    this.accessOrder = [];
  }

  /**
   * Updates the access order for LRU eviction
   * @param {string} key - Key that was accessed
   */
  updateAccessOrder(key) {
    this.accessOrder = this.accessOrder.filter(k => k !== key);
    this.accessOrder.push(key);
  }

  /**
   * Evicts oldest entries if cache exceeds max size
   */
  evictIfNeeded() {
    while (this.cache.size >= MAX_CACHE_SIZE && this.accessOrder.length > 0) {
      const oldestKey = this.accessOrder.shift();
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Gets cache statistics for debugging
   * @returns {Object} Cache statistics
   */
  getStats() {
    let expiredCount = 0;
    const now = Date.now();

    this.cache.forEach(entry => {
      if (now > entry.expiresAt) {
        expiredCount++;
      }
    });

    return {
      size: this.cache.size,
      maxSize: MAX_CACHE_SIZE,
      expiredCount,
      defaultTtlMs: DEFAULT_TTL_MS,
    };
  }
}

// Singleton instance
export const apiCache = new ApiCache();

/**
 * Higher-order function that wraps an API call with caching
 * @param {Function} apiFn - API function to wrap
 * @param {Object} options - Cache options
 * @param {string} options.keyPrefix - Cache key prefix
 * @param {number} options.ttlMs - Cache TTL in milliseconds
 * @param {Function} options.getKeyParams - Function to extract key params from API params
 * @returns {Function} Wrapped function with caching
 */
export const withCache = (apiFn, options = {}) => {
  const { keyPrefix = 'api', ttlMs = DEFAULT_TTL_MS, getKeyParams = params => params } = options;

  return async params => {
    const keyParams = getKeyParams(params);
    const cacheKey = apiCache.generateKey(keyPrefix, keyParams);

    // Check cache first
    const cachedValue = apiCache.get(cacheKey);
    if (cachedValue !== undefined) {
      return cachedValue;
    }

    // Call API and cache result
    const result = await apiFn(params);
    apiCache.set(cacheKey, result, ttlMs);

    return result;
  };
};

/**
 * Invalidates client-related cache entries
 * @param {string|number} clientId - Client ID to invalidate
 */
export const invalidateClientCache = clientId => {
  if (clientId) {
    apiCache.invalidateByPrefix(`client:${clientId}`);
    apiCache.invalidateByPrefix(`clientTags:${clientId}`);
    apiCache.invalidateByPrefix(`clientRelationships:${clientId}`);
  }
};
